// Apolo IDE — CodeMirror 6 inline completion (ghost text) — Sprint v11 E5.
//
// Implements ghost text rendering and accept-on-Tab via:
//   - StateField holds the current suggestion {from, text}
//   - StateEffect lets external code (the fetch loop) push a new suggestion
//   - EditorView.decorations renders the suggestion as a Decoration.widget
//   - keymap binds Tab to accept (when suggestion is present) + Esc to clear
//
// Fetching is driven by a separate side-effect plugin that watches typing,
// debounces, calls the Worker /api/code-ide/completion endpoint, and
// dispatches the SetCompletion effect when the response is ready.

import { StateField, StateEffect } from '@codemirror/state';
import { Decoration, EditorView, ViewPlugin, WidgetType, keymap } from '@codemirror/view';
import { Prec } from '@codemirror/state';
import { streamSSE } from './sseClient.js';

const DEBOUNCE_MS = 350;
const LRU_LIMIT = 50;

const setCompletion = StateEffect.define();
const clearCompletion = StateEffect.define();

class GhostWidget extends WidgetType {
  constructor(text) {
    super();
    this.text = text;
  }
  eq(other) { return other.text === this.text; }
  toDOM() {
    const span = document.createElement('span');
    span.textContent = this.text;
    span.className = 'apolo-ghost-completion';
    span.setAttribute('aria-hidden', 'true');
    span.style.opacity = '0.45';
    span.style.fontStyle = 'italic';
    span.style.pointerEvents = 'none';
    return span;
  }
  ignoreEvent() { return true; }
}

const completionField = StateField.define({
  create() { return { from: null, text: null }; },
  update(value, tr) {
    if (tr.docChanged && value.from !== null) {
      // The cursor has moved; bail out so a stale suggestion never inserts.
      return { from: null, text: null };
    }
    for (const e of tr.effects) {
      if (e.is(setCompletion)) {
        return { from: e.value.from, text: e.value.text };
      }
      if (e.is(clearCompletion)) {
        return { from: null, text: null };
      }
    }
    return value;
  },
  provide: (f) => EditorView.decorations.from(f, (state) => {
    if (state.from === null || !state.text) return Decoration.none;
    return Decoration.set([
      Decoration.widget({ widget: new GhostWidget(state.text), side: 1 }).range(state.from),
    ]);
  }),
});

const acceptCompletionCmd = (view) => {
  const sel = view.state.field(completionField);
  if (sel.from === null || !sel.text) return false;
  view.dispatch({
    changes: { from: sel.from, to: sel.from, insert: sel.text },
    selection: { anchor: sel.from + sel.text.length },
    effects: clearCompletion.of(null),
  });
  return true;
};

const rejectCompletionCmd = (view) => {
  const sel = view.state.field(completionField);
  if (sel.from === null) return false;
  view.dispatch({ effects: clearCompletion.of(null) });
  return true;
};

const completionKeymap = Prec.highest(keymap.of([
  { key: 'Tab', run: acceptCompletionCmd },
  { key: 'Escape', run: rejectCompletionCmd },
]));

function makeFetcher({ getOptions, signalAccessor }) {
  const cache = new Map(); // LRU keyed by `${language}\n${prefix}\n${suffix}`

  function cacheGet(key) {
    if (!cache.has(key)) return null;
    const v = cache.get(key);
    cache.delete(key);
    cache.set(key, v);
    return v;
  }

  function cacheSet(key, value) {
    if (cache.has(key)) cache.delete(key);
    cache.set(key, value);
    while (cache.size > LRU_LIMIT) cache.delete(cache.keys().next().value);
  }

  return async function fetchCompletion(prefix, suffix, language, filePath, onText) {
    const key = `${language || ''}\n${prefix}\n${suffix || ''}`;
    const cached = cacheGet(key);
    if (cached) {
      onText(cached);
      return cached;
    }

    let accumulator = '';
    const controller = new AbortController();
    signalAccessor.controller = controller;
    const opts = getOptions();
    const result = await streamSSE('/api/code-ide/completion', {
      body: {
        prefix,
        suffix,
        language,
        file_path: filePath,
        model_id: opts.modelId,
      },
      signal: controller.signal,
      onChunk: (chunk) => {
        if (chunk?.type === 'text' && typeof chunk.delta === 'string') {
          accumulator += chunk.delta;
          onText(accumulator);
        }
      },
    });
    if (result.ok && accumulator) {
      cacheSet(key, accumulator);
    }
    return accumulator;
  };
}

/**
 * Build the inline completion extension bundle.
 *
 * @param {{
 *   enabled: () => boolean,
 *   getOptions: () => { modelId?: string, language?: string, filePath?: string },
 * }} config
 */
export function inlineCompletionExtension({ enabled, getOptions }) {
  const signalAccessor = { controller: null };
  const fetchCompletion = makeFetcher({ getOptions, signalAccessor });

  const fetcherPlugin = ViewPlugin.fromClass(
    class {
      constructor(view) {
        this.view = view;
        this.timer = null;
        this.lastFiredAt = 0;
      }
      update(upd) {
        if (!enabled?.()) return;
        if (!upd.docChanged && !upd.selectionSet) return;
        // Cancel any in-flight fetch on every keystroke — stale completions
        // shouldn't land while the user is typing.
        signalAccessor.controller?.abort();
        signalAccessor.controller = null;
        this.view.dispatch({ effects: clearCompletion.of(null) });
        if (this.timer) clearTimeout(this.timer);
        this.timer = setTimeout(() => this.fire(), DEBOUNCE_MS);
      }
      async fire() {
        if (!enabled?.()) return;
        const view = this.view;
        const state = view.state;
        const head = state.selection.main.head;
        if (state.selection.main.from !== state.selection.main.to) return;
        const docLen = state.doc.length;
        const prefix = state.doc.sliceString(Math.max(0, head - 2000), head);
        const suffix = state.doc.sliceString(head, Math.min(docLen, head + 500));
        if (!prefix.trim()) return;
        const opts = getOptions();
        const text = await fetchCompletion(prefix, suffix, opts.language || '', opts.filePath || '', (acc) => {
          // Render incrementally as tokens arrive.
          if (state !== view.state) return; // doc changed mid-stream
          view.dispatch({ effects: setCompletion.of({ from: head, text: acc }) });
        });
        if (!text) {
          view.dispatch({ effects: clearCompletion.of(null) });
        }
      }
      destroy() {
        if (this.timer) clearTimeout(this.timer);
        signalAccessor.controller?.abort();
      }
    },
  );

  return [completionField, completionKeymap, fetcherPlugin];
}
